import React, { FormEvent, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSearchParams } from 'react-router-dom';
import Seo from '../components/Seo';
import { apps } from '../data/apps';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT ?? '/api/send-contact';

const EXTRA_APP_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const APP_OPTIONS = [
  { value: '', label: 'Select an app (optional)' },
  ...apps.map((app) => ({ value: app.id, label: app.name })),
  ...EXTRA_APP_OPTIONS,
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

function resolveAppParam(param: string | null): string {
  if (!param) {
    return '';
  }

  const normalized = param.trim().toLowerCase();
  const byId = apps.find((app) => app.id === normalized);
  if (byId) {
    return byId.id;
  }

  const byName = apps.find((app) => app.name.toLowerCase() === normalized);
  if (byName) {
    return byName.id;
  }

  if (EXTRA_APP_OPTIONS.some((option) => option.value === normalized)) {
    return normalized;
  }

  return '';
}

function getAppLabel(appId: string): string {
  if (!appId) {
    return '';
  }

  const listed = APP_OPTIONS.find((option) => option.value === appId);
  return listed?.label ?? appId;
}

function Contact() {
  const [searchParams] = useSearchParams();
  const appFromUrl = resolveAppParam(searchParams.get('app'));

  const [email, setEmail] = useState('');
  const [app, setApp] = useState(appFromUrl);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    setApp(appFromUrl);
  }, [appFromUrl]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage('');

    if (!RECAPTCHA_SITE_KEY) {
      setStatus('error');
      setErrorMessage(
        'Contact form is not configured yet. Please email dreamcode.app@gmail.com directly.'
      );
      return;
    }

    const recaptchaToken = recaptchaRef.current?.getValue();
    if (!recaptchaToken) {
      setStatus('error');
      setErrorMessage('Please complete the reCAPTCHA check.');
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          app: app ? getAppLabel(app) : undefined,
          message,
          recaptchaToken,
        }),
      });

      const data = (await response.json().catch(() => ({}))) as { error?: string };

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setStatus('success');
      setEmail('');
      setApp(appFromUrl);
      setMessage('');
      recaptchaRef.current?.reset();
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error && err.message
          ? err.message
          : 'Something went wrong. Please try again or email dreamcode.app@gmail.com.'
      );
      recaptchaRef.current?.reset();
    }
  }

  return (
    <section className="App-contact">
      <Seo
        title="Contact — Dreamcode"
        description="Contact Dreamcode for questions, feedback, or app support. We build NumGen Pro, SG Bus & MRT, Rain Radar & Alerts, Snack Snake, and PDF Desk."
        path="/contact"
      />
      <div className="App-contact-intro">
        <h1>Contact us</h1>
        <p>Questions, feedback, or support — send a message and we&apos;ll get back to you.</p>
      </div>

      {status === 'success' ? (
        <div className="App-contact-success" role="status">
          <p>Thanks — your message was sent. We&apos;ll reply to your email soon.</p>
          <button type="button" className="App-contact-submit" onClick={() => setStatus('idle')}>
            Send another message
          </button>
        </div>
      ) : (
        <form className="App-contact-form" onSubmit={handleSubmit}>
          <label className="App-contact-field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
              disabled={status === 'submitting'}
            />
          </label>

          <label className="App-contact-field">
            <span>
              App <span className="App-contact-optional">(optional)</span>
            </span>
            <select
              name="app"
              value={app}
              onChange={(e) => setApp(e.target.value)}
              disabled={status === 'submitting'}
            >
              {APP_OPTIONS.map((option) => (
                <option key={option.value || 'none'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="App-contact-field">
            <span>Message</span>
            <textarea
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={6}
              maxLength={5000}
              placeholder="How can we help?"
              disabled={status === 'submitting'}
            />
          </label>

          {RECAPTCHA_SITE_KEY ? (
            <div className="App-contact-recaptcha">
              <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} />
            </div>
          ) : (
            <p className="App-contact-hint">
              reCAPTCHA is not configured. Set <code>REACT_APP_RECAPTCHA_SITE_KEY</code> to enable
              the form.
            </p>
          )}

          {status === 'error' && errorMessage && (
            <p className="App-contact-error" role="alert">
              {errorMessage}
            </p>
          )}

          <button
            type="submit"
            className="App-contact-submit"
            disabled={status === 'submitting' || !RECAPTCHA_SITE_KEY}
          >
            {status === 'submitting' ? 'Sending…' : 'Send message'}
          </button>
        </form>
      )}
    </section>
  );
}

export default Contact;
